import { keepPreviousData, queryOptions } from '@tanstack/react-query'
import { z } from 'zod'

import { api } from '@/shared/api/http'
import {
  PAGE_DEFAULT_LIMIT,
  type InboxItemOrderBy,
  type InboxItemOut,
  type Page,
  type ScheduleOut,
} from '@/shared/api/types'
import { isMoney, type Money, sumMatches, toCents } from '@/shared/money/money'

/** payable = a pagar; receivable = a receber. Rascunho pode não ter ainda. */
export type InboxItemKind = 'payable' | 'receivable'
export type InboxItemStatus = 'pending' | 'processed' | 'discarded'

export const inboxKeys = {
  all: () => ['inbox-items'] as const,
  lists: () => [...inboxKeys.all(), 'list'] as const,
  list: (p: InboxListParams) => [...inboxKeys.lists(), p] as const,
} as const

export interface InboxListParams {
  readonly status?: InboxItemStatus
  /** Substring, sem diferenciar caixa, na descrição do rascunho. */
  readonly search?: string
  readonly order_by?: InboxItemOrderBy
  readonly limit?: number
  readonly offset?: number
}

/**
 * Itens da caixa de entrada. O backend traz TODOS os status por padrão
 * (processados/descartados são o histórico da triagem) — a tela filtra.
 */
export const inboxItemsQueryOptions = (p: InboxListParams = {}) =>
  queryOptions({
    queryKey: inboxKeys.list(p),
    queryFn: ({ signal }) =>
      api.get<Page<InboxItemOut>>(
        '/v1/inbox-items',
        { ...p, limit: p.limit ?? PAGE_DEFAULT_LIMIT },
        signal,
      ),
    placeholderData: keepPreviousData,
  })

const moneyField = z.string().refine((v) => isMoney(v) && toCents(v) > 0, {
  message: 'validation:amountPositive',
})

/**
 * Rascunho: SÓ `description` é obrigatória.
 *
 * É o ponto do módulo — anotar rápido o que chegou ("boleto da gráfica") e
 * completar depois. `amount`/`kind`/`due_date` ficam pendentes até a hora de
 * processar, onde viram obrigatórios (o merge item+payload precisa resolvê-los).
 */
export const inboxItemFormSchema = z.object({
  description: z.string().trim().min(1, 'validation:required').max(500, 'validation:maxLength'),
  amount: z.literal('').or(moneyField),
  kind: z.literal('').or(z.enum(['payable', 'receivable'])),
  dueDate: z.literal('').or(z.iso.date('validation:invalidDate')),
})

export type InboxItemFormValues = z.infer<typeof inboxItemFormSchema>

/** POST: vazio vira AUSENTE (campo do rascunho simplesmente não foi informado). */
export function toInboxCreateBody(v: InboxItemFormValues): Record<string, string> {
  return {
    description: v.description,
    ...(v.amount !== '' && { amount: v.amount }),
    ...(v.kind !== '' && { kind: v.kind }),
    ...(v.dueDate !== '' && { due_date: v.dueDate }),
  }
}

/**
 * PATCH: vazio vira `null` (LIMPA o campo do rascunho) — diferente do POST.
 * `description` é NOT NULL e vai sempre preenchida (null → 422).
 */
export interface InboxUpdateBody {
  readonly description: string
  readonly amount: string | null
  readonly kind: InboxItemKind | null
  readonly due_date: string | null
}

export function toInboxUpdateBody(v: InboxItemFormValues): InboxUpdateBody {
  return {
    description: v.description,
    amount: v.amount === '' ? null : v.amount,
    kind: v.kind === '' ? null : v.kind,
    due_date: v.dueDate === '' ? null : v.dueDate,
  }
}

export function createInboxItem(v: InboxItemFormValues): Promise<InboxItemOut> {
  return api
    .post<InboxItemOut>('/v1/inbox-items', { body: toInboxCreateBody(v) })
    .then((r) => r.data)
}

/** Só itens `pending` são editáveis (409 inbox_item_not_pending nos demais). */
export function updateInboxItem(id: string, v: InboxItemFormValues): Promise<InboxItemOut> {
  return api.patch<InboxItemOut>(`/v1/inbox-items/${id}`, toInboxUpdateBody(v))
}

/**
 * Descarta (transição terminal). Sem Idempotency-Key: não há efeito duplicável.
 * Repetir → 409. Não existe DELETE neste módulo — o histórico fica auditável.
 */
export function discardInboxItem(id: string): Promise<InboxItemOut> {
  return api.post<InboxItemOut>(`/v1/inbox-items/${id}/discard`, {}).then((r) => r.data)
}

// --- processar: rascunho → agendamento -------------------------------------

const rateioItem = z.object({
  categoryId: z.uuid('validation:required'),
  amount: moneyField,
})

/**
 * Processar exige o que o rascunho podia não ter: `kind`, `amount` e
 * `due_date` PRECISAM estar resolvidos (o backend responde 422
 * `inbox_item_incomplete` se o merge item+payload não fechar), mais o
 * contato e o rateio de um agendamento normal.
 *
 * O campo do valor chama-se `amount` para o SplitEditor genérico observá-lo.
 */
export const inboxProcessFormSchema = z
  .object({
    kind: z.enum(['payable', 'receivable']),
    amount: moneyField,
    dueDate: z.iso.date('validation:invalidDate'),
    stakeholderId: z.uuid('validation:required'),
    categories: z.array(rateioItem).min(1).max(100),
    costCenterId: z.string(),
    expectedDate: z.literal('').or(z.iso.date('validation:invalidDate')),
    description: z.string().trim().max(500),
  })
  .refine(
    (d) =>
      sumMatches(
        d.amount as Money,
        d.categories.map((c) => c.amount as Money),
      ),
    { path: ['categories'], message: 'validation:splitMismatch' },
  )
  .refine((d) => new Set(d.categories.map((c) => c.categoryId)).size === d.categories.length, {
    path: ['categories'],
    message: 'validation:duplicateCategory',
  })

export type InboxProcessFormValues = z.infer<typeof inboxProcessFormSchema>

export interface InboxProcessBody {
  readonly stakeholder_id: string
  readonly categories: readonly { category_id: string; amount: string }[]
  readonly kind: InboxItemKind
  readonly amount: string
  readonly due_date: string
  readonly cost_center_id?: string
  readonly expected_date?: string
  readonly description?: string
}

export function toInboxProcessBody(v: InboxProcessFormValues): InboxProcessBody {
  return {
    stakeholder_id: v.stakeholderId,
    categories: v.categories.map((c) => ({ category_id: c.categoryId, amount: c.amount })),
    kind: v.kind,
    amount: v.amount,
    due_date: v.dueDate,
    ...(v.costCenterId !== '' && { cost_center_id: v.costCenterId }),
    ...(v.expectedDate !== '' && { expected_date: v.expectedDate }),
    ...(v.description !== '' && { description: v.description }),
  }
}

export interface InboxProcessResult {
  readonly inbox_item: InboxItemOut
  readonly schedule: ScheduleOut
}

/**
 * `POST /v1/inbox-items/{id}/process` — EFEITO FINANCEIRO (cria o agendamento):
 * exige Idempotency-Key. 201 = processou; 200 = replay do mesmo par.
 */
export function processInboxItem(
  id: string,
  body: InboxProcessBody,
  idempotencyKey: string,
): Promise<{ data: InboxProcessResult; created: boolean }> {
  return api.post<InboxProcessResult>(`/v1/inbox-items/${id}/process`, {
    body,
    idempotencyKey,
    operationPath: '/v1/inbox-items/{inbox_item_id}/process',
  })
}
